import {
  Button,
  ConfirmationDialog,
  Input,
  SuggestionSelect,
} from '@descomplica/foundation-ui-components';
import { useViewModel } from '@descomplica/microfrontend-react';
import { observer } from 'mobx-react';
import React from 'react';
import styled from 'styled-components';

import { EFileExtension, exportEmailSheet } from '@/shared/application/utils/sheets';
import { Page } from '@/shared/presentation/components/ui/Page';
import { TagInput } from '@/shared/presentation/components/ui/TagInput';

import InvitesFeedbackModal from './components/InvitesFeedbackModal';
import LinkDownloadIo from './components/LinkDownload';
import MultipleEmailInput from './components/MultipleEmailInput';
import { CreateInvitationViewModel } from './CreateInvitation.vm';

const FormBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-top: 8px;
`;

const ConfirmButton = styled(Button)`
  align-self: end;
`;

const InviteForm = observer(() => {
  const vm = useViewModel(CreateInvitationViewModel);

  return (
    <Form>
      <MultipleEmailInput
        label="Emails"
        placeholder="aluno@descomplica.com.br"
        values={vm.form.values.emails.map((email) => ({
          guid: email.guid,
          name: email.name,
        }))}
        setValues={(values) => {
          vm.form.setValueAndValidateValue('emails', values);
        }}
        onError={(invalidEmails, validEmails) => vm.handleInvalidError(invalidEmails, validEmails)}
      />
      <TagInput
        label="Escopos"
        name="scopes"
        isMulti
        onChange={(value) => {
          vm.form.setValueAndValidateValue('scopes', value);
        }}
        values={vm.form.values.scopes.map((scope) => ({
          guid: scope.guid,
          name: scope.name,
        }))}
        options={vm.scopes.map((scope) => ({
          guid: scope.guid,
          name: scope.name,
        }))}
      />
      <SuggestionSelect
        label="Papel"
        placeholder="Definir papel do grupo"
        onChange={(value) => {
          vm.form.setValueAndValidateValue('role', value);
        }}
        value={vm.form.values.role}
        availableOptions={vm.roles.map((role) => ({
          guid: role.guid,
          name: role.name,
        }))}
      />
      <Input
        animated
        feedbackMessage="Insira as tags separadas por vírgulas Ex: turma-1, enem-2024"
        label="Tags"
        name="tags"
        placeholder="Identificação da turma"
        value={vm.form.values.tags}
        onChange={(value) => vm.form.setValueAndValidateValue('tags', value)}
      />
    </Form>
  );
});

const DownloadButton = observer(() => {
  const vm = useViewModel(CreateInvitationViewModel);

  const options = [
    {
      label: '.csv',
      icon: 'invite',
      onClick: () => {
        exportEmailSheet(vm.invalidEmails, EFileExtension.CSV);
      },
    },
    {
      label: '.xlsx',
      icon: 'invite',
      onClick: () => {
        exportEmailSheet(vm.invalidEmails, EFileExtension.XLSX);
      },
    },
  ];

  return (
    <>
      <LinkDownloadIo buttonComponent={<a>Baixar lista com emails inválidos</a>} items={options} />
    </>
  );
});

export const CreateInvitationScreen = observer(() => {
  const vm = useViewModel(CreateInvitationViewModel);

  const importData = () => {
    vm.importEmails(vm.emailsPreImported);
    vm.setIsOpen(false);
  };

  return (
    <Page
      title="Convites"
      subtitle="Envie os convites do sistema - Quantidade máxima de 10.000 emails"
      isLoading={vm.isLoading}
      hasError={vm.hasError}
    >
      <FormBox>
        <InviteForm />
        <ConfirmButton
          disabled={
            vm.form.values.emails.length == 0 ||
            vm.form.values.scopes.length == 0 ||
            vm.form.values.role == null
          }
          onClick={() => vm.create()}
          size="small"
        >
          Gerar convites
        </ConfirmButton>
      </FormBox>
      {vm.isOpen && (
        <ConfirmationDialog
          isOpen={vm.isOpen}
          isSendingConfirmation={false}
          title={
            vm.invalidEmails.length === 1
              ? 'Foi encontrado 1 email inválido'
              : `Foram encontrados ${vm.invalidEmails.length} emails inválidos`
          }
          confirmButtonLabel="Continuar a importação"
          description={<DownloadButton />}
          onConfirm={importData}
          onCancel={() => vm.setIsOpen(false)}
        />
      )}
      {vm.isInvitesFeedbackOpen && (
        <InvitesFeedbackModal isOpen={vm.isInvitesFeedbackOpen} isLoading={vm.isSubmitting} />
      )}
    </Page>
  );
});
